<template>
    <div class="col-sm-12 nopads">
        <div class="search-form">
            <div
                class="col-sm-12 search-form-title"
                style="margin-left: .5em"
            >
                <font-awesome-icon icon="search" />{{ $t('common.search') }}
            </div>
            <form
                id="fuel-report-search"
                class="mt-2 p-2"
                @submit.prevent="onSubmit"
                @reset="onReset"
            >
                <div class="row">
                    <div class="col-sm-4">
                        <open-contract-filter
                            :from="fromDate"
                            :to="toDate"
                            :contract="contract"
                            :apply-padding="false"
                            :row-view="false"
                            :show-only-my-contracts-option="true"
                            :show-title="true"
                            :show-company="false"
                            :required="!contract"
                            @contract-selected="setContract"
                        />
                    </div>
                    <div class="col-sm-4">
                        <contract-order-multiselect
                            ref="orderFilter"
                            :contract-ids="contract ?  [contract.id] : []"
                            :single-mode="true"
                            @orders-selected="setOrder"
                        />
                    </div>
                    <div class="col-sm-4 pt-1">
                        <span class="search-title">{{ $t('trip_list.fuel_type') }}</span>
                        <select
                            id="fuel"
                            v-model="fuelType"
                            class="form-select form-select-sm"
                        >
                            <option :value="null">
                                {{ $t('trip_list.fuel_type') }}
                            </option>
                            <option
                                v-for="option in fuelTypes"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.text }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="row pt-2">
                    <div class="col-sm-4 px-4">
                        <span class="search-title">{{ $t('delivery_note.date') }} - {{
                            $t('trip_list.road_sec_from')
                        }}</span>
                        <date-time-picker
                            v-model:date="fromDate"
                            class="routa-input"
                            format="dd.MM.yyyy HH:mm"
                            :enable-time-picker="true"
                            :placeholder="$t('observations_videos.search_input_format')"
                        />
                    </div>
                    <div class="col-sm-4">
                        <span class="search-title">{{ $t('delivery_note.date') }} - {{
                            $t('trip_list.road_sec_to')
                        }}</span>
                        <date-time-picker
                            v-model:date="toDate"
                            class="routa-input"
                            format="dd.MM.yyyy HH:mm"
                            :enable-time-picker="true"
                            :placeholder="$t('observations_videos.search_input_format')"
                        />
                    </div>
                </div>
                <div class="col-sm-12 button-container">
                    <button
                        type="reset"
                        class="btn btn-danger form-button"
                    >
                        {{ $t('delivery_note.clear') }}
                    </button>
                    <button
                        type="submit"
                        class="btn btn-primary form-button"
                        :disabled="!contract"
                    >
                        {{ $t('common.search') }}
                    </button>
                </div>
            </form>
        </div>
        <div
            v-if="isLoading"
            id="loader"
            class="spinner-loader"
        />
    </div>
</template>

<script>
import OpenContractFilter from "@/components/contract/OpenContractFilter.vue";
import MaterialStorageFilter from "@/components/materialstorage/MaterialStorageFilter.vue";
import DateTimePicker from "@/components/common/DateTimePicker.vue";
import {restApi} from "@/components/mixins/RestApiMixin.js";
import {timeUtils} from "@/components/mixins/TimeUtils.js";
import {orderMixin} from "@/components/mixins/OrderMixin.js";
import {vehicleHelper, vehicleFuel} from "../../mixins/VehicleMixin.js";
import ContractOrderMultiselect from "../../order/ContractOrderMultiselect.vue";

export default {
    name: "FuelReportSearch",
    components: {ContractOrderMultiselect, DateTimePicker, MaterialStorageFilter, OpenContractFilter},
    mixins: [restApi, timeUtils, orderMixin, vehicleHelper],
    props: {
        contracts: {
            type: Array,
            default() {
                return []
            }
        },
        showOnlyMyContractsOption: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            fromDate: this.loginTime ? this.loginTime.toISOString() : this.getMonthStart(), // Add fallback
            toDate: null,
            contract: null,
            order: null,
            loading: 0,
            storage: null,
            fuelType: null,
        }
    },
    computed: {
        fuelTypes() {
            return [
                {value: vehicleFuel.UNKNOWN, text: this.getFuelTypeName(vehicleFuel.UNKNOWN)},
                {value: vehicleFuel.FUEL_98E, text: this.getFuelTypeName(vehicleFuel.FUEL_98E)},
                {value: vehicleFuel.FUEL_95E10, text: this.getFuelTypeName(vehicleFuel.FUEL_95E10)},
                {value: vehicleFuel.DIESEL, text: this.getFuelTypeName(vehicleFuel.DIESEL)},
                {value: vehicleFuel.HVO, text: this.getFuelTypeName(vehicleFuel.HVO)},
                {value: vehicleFuel.FUEL_OIL, text: this.getFuelTypeName(vehicleFuel.FUEL_OIL)},
                {value: vehicleFuel.GAS_CNG, text: this.getFuelTypeName(vehicleFuel.GAS_CNG)},
                {value: vehicleFuel.GAS_CBG, text: this.getFuelTypeName(vehicleFuel.GAS_CBG)},
                {value: vehicleFuel.GAS_LNG, text: this.getFuelTypeName(vehicleFuel.GAS_LNG)},
                {value: vehicleFuel.HYBRID, text: this.getFuelTypeName(vehicleFuel.HYBRID)},
                {value: vehicleFuel.ELECTRIC, text: this.getFuelTypeName(vehicleFuel.ELECTRIC)},
                {value: vehicleFuel.OTHER, text: this.getFuelTypeName(vehicleFuel.OTHER)}
            ]
        }
    },
    mounted() {
        if (!this.fromDate) {
            this.fromDate = this.getMonthStart()
        }
    },
    methods: {
        setContract: function (contract) {
            this.contract = contract ? contract : null
        },
        setOrder(order) {
            this.order = order
        },
        onSubmit: function () {
            let params = {}
            if (this.fromDate !== null && this.fromDate.trim()) {
                params.timeFrom = new Date(this.fromDate).getTime()
            }
            if (this.toDate !== null && this.toDate.trim()) {
                params.timeTo = new Date(this.toDate).getTime()
            }
            if (this.contract !== null) {
                params.contract = this.contract.id
            }
            if (this.order) {
                params.order = this.order.id
            }
            if (this.fuelType !== null) {
                params.fuelType = this.fuelType
            }
            this.$emit('search', params)
        },
        onReset: function () {
            this.orders = []
            this.fromDate = null
            this.toDate = null
            this.contract = null
            this.fuelType = null
        },

        getOptionSearchParams() {
            return {
                from: this.fromDate,
                to: this.toDate,
                contract: this.contract?.id,
                orders: this.order ? [this.order.id] : null,
                companies: this.companyParam ? this.companyParam.map(cParam => cParam.value) : null
            }
        }
    }
}
</script>

<style lang="css" scoped>
.spinner-loader {
    position: absolute;
    top: 50vh;
    left: 45vw;
    margin-left: auto;
    margin-right: auto;
    z-index: 1000;
    border: .3em solid #404041;
    border-radius: 50%;
    border-top: .3em solid #7FC000;
    width: 3em;
    height: 3em;
    -webkit-animation: spin 2s linear infinite;
    animation: spin 1s linear infinite;
}
</style>
